import React, { useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { useSelector, shallowEqual } from 'react-redux';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Hydrate } from 'react-query/hydration';
import { ReactQueryDevtools } from 'react-query/devtools';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Routes from '../Routes';
import Header from './Header';
import messages from '../../assets/messages';
import renderLocale from '../../_shared/helpers/renderLocale';
import ReducerConstants from '../../_shared/constants/reducers';
import timeouts from '../../_shared/constants/timeouts';
import { CACHE } from '../../_shared/constants/queryDefinition';
import usePersistQueryCache from '../../_shared/hooks/usePersistQueryCache';
import useErrorTracking from '../../_shared/hooks/error/useErrorTracking';
import IndexDBStoreService from '../../services/IndexDBStoreService';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      refetchInterval: false,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retry: false
    },
    mutations: {
      staleTime: Infinity,
      refetchInterval: false,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false
    }
  }
});

const App = () => {
  useErrorTracking();
  const auth = useSelector(state => state[ReducerConstants.AUTH], shallowEqual);
  const locale = renderLocale(auth);
  usePersistQueryCache(queryClient);

  const [dehyDratedState, setDehyDratedState] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Rehydrate react query
    async function rehydrate() {
      const queryValue = await IndexDBStoreService.getItem(CACHE.query);

      if (queryValue) {
        setDehyDratedState(queryValue);
      }
      setIsLoading(false);
    }

    rehydrate();
  }, []);

  return (
    <IntlProvider locale={locale} messages={messages[locale]}>
      <QueryClientProvider client={queryClient}>
        <Hydrate state={dehyDratedState}>
          <main data-testid="main">
            <ToastContainer
              position="top-center"
              autoClose={timeouts.INFO}
              newestOnTop
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
            <Header />

            {!isLoading && <Routes />}
          </main>
        </Hydrate>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </IntlProvider>
  );
};

export default App;
